import { supabase } from "@/integrations/supabase/client";
import { authReturnUrl } from "@/hooks/useAuth";
import { secondsUntilResend } from "@/lib/auth-resend";

/**
 * A fresh email link for someone whose link expired.
 *
 * 19 browsers landed on an expired or already-used link (`otp_expired`) from
 * Sep 1 to Oct 24, 2026, and 4 of them went on to sign in. The toast told
 * them to sign in with a password to be offered a new link, which is two
 * screens and a password for something one email address can do.
 *
 * `signInWithOtp` with `shouldCreateUser: false` covers both cases the person
 * cannot tell apart: an unconfirmed sign-up gets its confirmation email again,
 * a confirmed account gets a sign-in link. Either signs them in. It never
 * creates an account and never changes a password.
 *
 * The address is remembered on this device from the sign-up form, so a link
 * opened in the same browser needs one tap. Only 1 or 2 of those 19 browsers
 * had started the sign-up there (the rest opened the email elsewhere), so
 * `/auth?link=expired` asks for the address in one field otherwise.
 */
export const LINK_EMAIL_KEY = "wpm_link_email";
export const LINK_EMAIL_TTL_MS = 7 * 24 * 60 * 60 * 1000;

/** On `/auth`, "show the new-link form". */
export const EXPIRED_LINK_PARAM = "link";

export function rememberLinkEmail(email: string, now = Date.now()): void {
  try {
    localStorage.setItem(LINK_EMAIL_KEY, JSON.stringify({ email: email.trim().toLowerCase(), savedAt: now }));
  } catch {
    // Storage blocked: the new-link form asks for the address instead.
  }
}

export function recallLinkEmail(now = Date.now()): string | null {
  try {
    const raw = localStorage.getItem(LINK_EMAIL_KEY);
    if (!raw) return null;
    const stored = JSON.parse(raw) as { email?: unknown; savedAt?: unknown };
    if (typeof stored.email !== "string" || !stored.email.includes("@")) return null;
    if (typeof stored.savedAt !== "number" || now - stored.savedAt > LINK_EMAIL_TTL_MS) return null;
    return stored.email;
  } catch {
    return null;
  }
}

/** Once signed in, the address has done its job here. */
export function forgetLinkEmail(): void {
  try {
    localStorage.removeItem(LINK_EMAIL_KEY);
  } catch {
    // Nothing to do.
  }
}

export type LinkResendOutcome =
  | { kind: "sent" }
  | { kind: "throttled"; seconds: number }
  | { kind: "no_account" }
  | { kind: "error"; message: string };

export async function sendFreshLink(email: string, redirectPath?: string): Promise<LinkResendOutcome> {
  const { error } = await supabase.auth.signInWithOtp({
    email: email.trim(),
    options: { shouldCreateUser: false, emailRedirectTo: authReturnUrl(redirectPath) },
  });
  if (!error) return { kind: "sent" };
  const seconds = secondsUntilResend(error.message);
  if (seconds !== null) return { kind: "throttled", seconds };
  // What GoTrue answers `shouldCreateUser: false` for an address it has no user for.
  if (/signups not allowed/i.test(error.message)) return { kind: "no_account" };
  return { kind: "error", message: error.message };
}

export function describeLinkResend(outcome: LinkResendOutcome, email: string): string {
  switch (outcome.kind) {
    case "sent":
      return `We sent a new link to ${email}. Use the newest email: each one replaces the last.`;
    case "throttled":
      return `We just sent one. You can ask for another in ${outcome.seconds} second${outcome.seconds === 1 ? "" : "s"}.`;
    case "no_account":
      return `There's no account for ${email}. Check the address, or create a free account.`;
    case "error":
      return "We couldn't send a new link just now. Please try again in a minute.";
  }
}
